import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Home,
  Calendar,
  BookOpen,
  GraduationCap,
  FileText,
  Award,
  Settings,
  Plus,
  Sparkles,
  Send,
  Loader2,
  ChevronRight,
  Clock,
  Target,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";

interface AceCommandPaletteProps {
  onOpenQuickAdd?: () => void;
}

const quickPrompts = [
  { icon: Clock, label: "What's due this week?", prompt: "What assignments and exams do I have due this week?" },
  { icon: Target, label: "Today's priorities", prompt: "What should I focus on today? List my priorities based on deadlines and importance." },
  { icon: TrendingUp, label: "My current grades", prompt: "What are my current grades in all my classes?" },
  { icon: GraduationCap, label: "Upcoming exams", prompt: "When are my upcoming exams and what topics do they cover?" },
];

const navigationItems = [
  { icon: Home, label: "Dashboard", path: "/dashboard" },
  { icon: Calendar, label: "Calendar", path: "/calendar" },
  { icon: BookOpen, label: "Courses", path: "/courses" },
  { icon: FileText, label: "Materials", path: "/materials" },
  { icon: Award, label: "Grades", path: "/grades" },
  { icon: Settings, label: "Account", path: "/account" },
];

export function AceCommandPalette({ onOpenQuickAdd }: AceCommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [isAsking, setIsAsking] = useState(false);
  const [response, setResponse] = useState("");
  const [showResponse, setShowResponse] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const responseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResponse("");
      setShowResponse(false);
      setIsAsking(false);
    }
  }, [open]);

  // Auto-scroll response area
  useEffect(() => {
    if (responseRef.current) {
      responseRef.current.scrollTop = responseRef.current.scrollHeight;
    }
  }, [response]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const handleQuickAdd = () => {
    setOpen(false);
    onOpenQuickAdd?.();
  };

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const askAce = useCallback(async (question: string) => {
    if (!question.trim() || isAsking) return;

    setIsAsking(true);
    setShowResponse(true);
    setResponse("");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setResponse("Please sign in to ask Ace questions about your academic data.");
        setIsAsking(false);
        return;
      }

      const messages = [{ role: "user" as const, content: question }];

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ace-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            messages, 
            conversationId,
            quickMode: true // Request concise responses
          }),
        }
      );

      if (!resp.ok || !resp.body) {
        throw new Error("Failed to get response from Ace");
      }

      // Get conversation ID from header
      const newConversationId = resp.headers.get("X-Conversation-Id");
      if (newConversationId) {
        setConversationId(newConversationId);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIndex);
          buffer = buffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              setResponse((prev) => prev + content);
            }
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Ace error:", error);
      setResponse("Sorry, I couldn't process that request. Please try again.");
    } finally {
      setIsAsking(false);
    }
  }, [conversationId, isAsking]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (query.trim()) {
      askAce(query);
      setQuery("");
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    askAce(prompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && query.trim()) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-2 px-3 pt-3">
        <div className="flex items-center gap-2 text-primary">
          <Sparkles className="h-5 w-5" />
          <span className="font-semibold">Ask Ace</span>
        </div>
        <span className="text-xs text-muted-foreground">Your AI study assistant</span>
      </div>
      
      <div className="relative">
        <CommandInput
          placeholder="Ask anything about your classes, assignments, grades..."
          value={query}
          onValueChange={setQuery}
          onKeyDown={handleKeyDown}
          className="pr-10"
        />
        {query.trim() && (
          <Button
            size="icon"
            variant="ghost"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => handleSubmit()}
            disabled={isAsking}
          >
            {isAsking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>

      <CommandList className="max-h-[400px]">
        {/* AI Response Area */}
        {showResponse && (
          <>
            <div className="px-3 py-3">
              <div 
                ref={responseRef}
                className="rounded-lg bg-muted/50 p-4 max-h-[200px] overflow-y-auto"
              >
                {isAsking && !response && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Ace is thinking...</span>
                  </div>
                )}
                {response && (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown>{response}</ReactMarkdown>
                  </div>
                )}
              </div>
              {response && !isAsking && (
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowResponse(false);
                      setResponse("");
                    }}
                    className="text-xs"
                  >
                    Ask something else
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setOpen(false);
                      navigate("/ace");
                    }}
                    className="text-xs text-muted-foreground"
                  >
                    Open full chat <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              )}
            </div>
            <CommandSeparator />
          </>
        )}

        {/* Quick Prompts - Only show when not showing response */}
        {!showResponse && (
          <>
            <CommandEmpty>
              <div className="py-2 text-center text-sm text-muted-foreground">
                Ask Ace anything or try a quick prompt below
              </div>
            </CommandEmpty>

            <CommandGroup heading="Quick Prompts">
              {quickPrompts.map((item) => (
                <CommandItem
                  key={item.label}
                  onSelect={() => handleQuickPrompt(item.prompt)}
                  className="cursor-pointer"
                >
                  <item.icon className="mr-2 h-4 w-4 text-primary" />
                  <span>{item.label}</span>
                  <ChevronRight className="ml-auto h-3 w-3 text-muted-foreground" />
                </CommandItem>
              ))}
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Quick Navigation">
              {navigationItems.map((item) => (
                <CommandItem
                  key={item.path}
                  onSelect={() => handleNavigate(item.path)}
                >
                  <item.icon className="mr-2 h-4 w-4" />
                  <span>{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Quick Actions">
              <CommandItem onSelect={handleQuickAdd}>
                <Plus className="mr-2 h-4 w-4" />
                <span>Quick Add</span>
                <kbd className="pointer-events-none ml-auto inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  <span className="text-xs">⌘</span>N
                </kbd>
              </CommandItem>
              <CommandItem onSelect={toggleTheme}>
                <span className="mr-2">
                  {theme === "dark" ? "☀️" : "🌙"}
                </span>
                <span>Toggle {theme === "dark" ? "Light" : "Dark"} Mode</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>

      <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
        <span>Press <kbd className="px-1 py-0.5 rounded bg-muted font-mono">Enter</kbd> to ask</span>
        <span>Press <kbd className="px-1 py-0.5 rounded bg-muted font-mono">Esc</kbd> to close</span>
      </div>
    </CommandDialog>
  );
}
